import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import useModalBackButton from '../hooks/useModalBackButton';
import FloatingField from '../components/FloatingField';
import {
  Edit3,
  Save,
  X,
  Clock,
  Activity,
  UserX,
  UserCheck,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  Copy,
  Loader2,
  Share2,
  ChevronDown,
  ChevronUp,
  Truck,
  Droplets,
  Sofa,
  Package,
  Home,
  Shield,
  Building,
  Trash2
} from 'lucide-react';
import { safeFetch } from '../utils/safeFetch';
import { API_URL } from '../config';
import { USER_STAGE_ORDER, STAGES_CONFIG, STAGE_BADGE_STYLES, getStagesFromRoles } from '../utils/stages';
import { useDryCleaningEnabled } from '../utils/featureFlags';
import { SkeletonBlock, UserDetailsSkeleton } from '../components/Skeleton';
import DepartmentSelector from '../components/DepartmentSelector';
import Dropdown from '../components/Dropdown';
import useToast from '../hooks/useToast';
import UserActivityModal from '../components/UserActivityModal';
import ModalShell from '../components/ModalShell';
import { haptic } from '../utils/vibration';
import { formatPersonName } from '../utils/personName';

// Функция форматирования телефона
const formatPhone = (value) => {
  let cleaned = value.replace(/\D/g, '');
  
  if (cleaned.startsWith('8')) {
    cleaned = '7' + cleaned.substring(1);
  } else if (!cleaned.startsWith('7') && cleaned.length > 0) {
    cleaned = '7' + cleaned;
  }
  
  if (cleaned.length > 11) {
    cleaned = cleaned.substring(0, 11);
  }
  
  let formatted = '+7';
  if (cleaned.length > 1) {
    formatted += ' (' + cleaned.substring(1, 4);
    if (cleaned.length > 4) {
      formatted += ') ' + cleaned.substring(4, 7);
      if (cleaned.length > 7) {
        formatted += '-' + cleaned.substring(7, 9);
        if (cleaned.length > 9) {
          formatted += '-' + cleaned.substring(9, 11);
        }
      }
    }
  }
  
  return formatted;
};

const cleanPhoneDigits = (phone = '') => phone.replace(/\D/g, '');

const isFullPhoneNumber = (phone = '') => {
  const digits = cleanPhoneDigits(phone);
  return digits.length === 11 && digits.startsWith('79');
};

const getPhoneError = (phone = '') => {
  const digits = cleanPhoneDigits(phone);
  if (!digits || digits === '7') return '';
  if (digits.length >= 2 && !digits.startsWith('79')) {
    return 'Номер должен начинаться с +79';
  }
  if (digits.length === 11 && !isFullPhoneNumber(phone)) {
    return 'Номер должен начинаться с +79 и содержать 11 цифр';
  }
  return '';
};

const UserDetailsCompact = ({ user: initialUser, onUserUpdated }) => {
  const isDryCleaningEnabled = useDryCleaningEnabled();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();
  const [editErrors, setEditErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [showRolesEdit, setShowRolesEdit] = useState(false);
  const [showMenuEdit, setShowMenuEdit] = useState(false);
  const [showDepartmentsEdit, setShowDepartmentsEdit] = useState(false); // Add this line
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showRestoreConfirmModal, setShowRestoreConfirmModal] = useState(false);
  const [assignmentImpact, setAssignmentImpact] = useState(null);
  const [blockImpact, setBlockImpact] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [checkingBlockImpact, setCheckingBlockImpact] = useState(false);
  const [checkingDeleteImpact, setCheckingDeleteImpact] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [passwordResetLink, setPasswordResetLink] = useState('');
  const [passwordResetExpiresAt, setPasswordResetExpiresAt] = useState('');
  const [passwordResetLogin, setPasswordResetLogin] = useState('');

  // Режим редактирования и модалки — отдельные уровни навигации.
  // Возврат из самой карточки обрабатывает постоянно смонтированный экран списка.
  useModalBackButton(isEditing, () => {
    haptic('close');
    setIsEditing(false);
  });

  useModalBackButton(showResetModal, () => {
    setShowResetModal(false);
    setPasswordResetLink('');
    setPasswordResetExpiresAt('');
    setPasswordResetLogin('');
  });
  useModalBackButton(showResetConfirmModal, () => setShowResetConfirmModal(false));
  useModalBackButton(showBlockModal, () => {
    setShowBlockModal(false);
    setBlockReason('');
    setBlockImpact(null);
  });
  useModalBackButton(showDeleteConfirmModal, () => {
    setShowDeleteConfirmModal(false);
    setDeleteImpact(null);
    setDeleteReason('');
  });
  useModalBackButton(showRestoreConfirmModal, () => setShowRestoreConfirmModal(false));
  useModalBackButton(!!assignmentImpact, () => { setAssignmentImpact(null); setPendingUpdateData(null); });

  // Add departments state
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [userDepartments, setUserDepartments] = useState([]); // For displaying user's current departments
  const [allDepartments, setAllDepartments] = useState([]); // Store all departments for name lookup
  
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    phone: '',
    user_type: '',
    is_hidden: false,
    works_with_tasks: false,
    can_access_deferred_orders: false,
    user_status: '',
    user_roles: '',
    available_menus: ''
  });

  const menuOptions = [
    { id: 'new-order', label: 'Новый заказ' },
    { id: 'my-orders', label: 'Мои заказы' },
    { id: 'tasks', label: 'Поручения' },
    { id: 'orders-log', label: 'Журнал заказов' },
    { id: 'risk-control', label: 'Контроль рисков' },
    { id: 'routes', label: 'Маршруты' },
    { id: 'expenses', label: 'Расходы' },
    { id: 'statistics', label: 'Статистика' },
    { id: 'management', label: 'Управление' },
    { id: 'news', label: 'Новости' },
    { id: 'settings', label: 'Настройки' }
  ];

  // Функции для безопасного копирования текста
  const copyToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        return fallbackCopyTextToClipboard(text);
      }
    }
    return fallbackCopyTextToClipboard(text);
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      return document.execCommand('copy');
    } catch (err) {
      return false;
    }
    finally {
      document.body.removeChild(textArea);
    }
  };

  const buildPasswordResetShareText = () => {
    const loginLine = passwordResetLogin ? `Логин: ${passwordResetLogin}\n` : '';
    const expiresLine = passwordResetExpiresAt
      ? `Ссылка действует до ${formatDate(passwordResetExpiresAt)}.\n`
      : '';
    return `${loginLine}Ссылка для установки нового пароля:\n${passwordResetLink}\n\n${expiresLine}Ссылка одноразовая.`;
  };

  const handleCopyResetLink = async () => {
    const copied = await copyToClipboard(passwordResetLink);
    if (copied) {
      showToast('Ссылка для сброса скопирована');
    } else {
      showToast('Не удалось скопировать ссылку', 'error');
    }
  };

  const handleShareResetLink = async () => {
    if (!passwordResetLink) return;
    const shareText = buildPasswordResetShareText();

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Ссылка для сброса пароля',
          text: shareText,
        });
        showToast('Ссылка готова к отправке');
        return;
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
      }
    }

    await handleCopyResetLink();
  };

  const userTypeLabels = {
    user: 'Пользователь',
    dispatcher: 'Диспетчер',
    admin: 'Администратор',
    developer: 'Разработчик'
  };

  const statusLabels = {
    active: 'Активен',
    blocked: 'Заблокирован'
  };

  const statusColors = {
    active: 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300',
    blocked: 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300'
  };

  const userTypeColors = {
    user: 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300',
    dispatcher: 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-300',
    admin: 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-300',
    developer: 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300'
  };
  const deferredAccessUserTypes = ['dispatcher', 'admin', 'developer'];
  const defaultDeferredAccessUserTypes = ['admin', 'developer'];
  const canUserTypeAccessDeferredOrders = (userType) => deferredAccessUserTypes.includes(String(userType || '').trim().toLowerCase());
  const hasDefaultDeferredOrdersAccess = (userType) => defaultDeferredAccessUserTypes.includes(String(userType || '').trim().toLowerCase());
  const hasDeferredOrdersAccess = (useUserData = false) => {
    const source = useUserData ? user : editData;
    if (!canUserTypeAccessDeferredOrders(source?.user_type)) return false;
    return typeof source?.can_access_deferred_orders === 'boolean'
      ? source.can_access_deferred_orders
      : hasDefaultDeferredOrdersAccess(source?.user_type);
  };

  // Конфигурация этапов (используем общие константы)
  const orderStages = Object.fromEntries(
    Object.entries(STAGES_CONFIG).map(([key, config]) => [
      key,
      {
        name: config.label,
        icon: config.icon,
        color: STAGE_BADGE_STYLES[config.color] || STAGE_BADGE_STYLES.blue
      }
    ])
  );
  const allStageRoleNames = USER_STAGE_ORDER
    .filter(key => isDryCleaningEnabled || key !== 'cleaning')
    .map(key => orderStages[key]?.name)
    .filter(Boolean);
  const getNormalizedStageRoleNames = (rolesValue) => {
    try {
      const roles = typeof rolesValue === 'string' ? JSON.parse(rolesValue || '[]') : (rolesValue || []);
      if (!Array.isArray(roles)) return [];
      if (roles.includes('all')) return allStageRoleNames;
      return getStagesFromRoles(roles)
        .filter(key => isDryCleaningEnabled || key !== 'cleaning')
        .map((key) => orderStages[key]?.name)
        .filter(Boolean);
    } catch (error) {
      console.error('Ошибка нормализации ролей этапов:', error);
      return [];
    }
  };
  const mobileInputClass = (field) => `w-full min-h-[44px] px-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 dark:text-gray-100 text-sm transition-colors ${
    editErrors[field]
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500/30 dark:border-red-500'
      : 'border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-blue-500/40'
  }`;
  const editSectionButtonClass = "flex min-h-[48px] items-center justify-between w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5 text-left shadow-sm transition-all active:scale-[0.99]";
  const editOptionClass = "flex min-h-[46px] items-center gap-2 rounded-xl border p-2.5 cursor-pointer transition-all active:scale-[0.98]";

  const toggleEditSection = (section, setter) => {
    haptic(section ? 'close' : 'open');
    setter(!section);
  };

  const startEditing = () => {
    haptic('open');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    haptic('close');
    setIsEditing(false);
  };

  // Получение доступных этапов пользователя в фиксированном порядке
  const getUserStages = (userRoles) => {
    if (!userRoles) return [];
    try {
      const roles = typeof userRoles === 'string' ? JSON.parse(userRoles) : userRoles;
      if (!Array.isArray(roles)) return [];
      const userStageKeys = new Set(getStagesFromRoles(roles));

      return USER_STAGE_ORDER
        .filter(key => isDryCleaningEnabled || key !== 'cleaning')
        .filter(key => userStageKeys.has(key))
        .map(key => ({ key, ...orderStages[key] }));
    } catch (error) {
      console.error('Error parsing user roles:', error);
      return [];
    }
  };

  // Состояние спойлеров
  const [expandedSections, setExpandedSections] = useState({
    activity: false,
    departments: false,
    stages: false
  });

  useEffect(() => {
    if (initialUser) {
      fetchUserDetails(initialUser.id);
    }
    // Get current user ID to prevent self-blocking
    const token = localStorage.getItem('token');
    if (token) {
      checkCurrentUser();
    }
  }, [initialUser]);

  const checkCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      
      const data = await safeFetch(`${API_URL}/api/check-auth`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (data && data.user_id) {
        setCurrentUserId(data.user_id);
      } else {
        console.error('Invalid response from check-auth:', data);
      }
    } catch (err) {
      console.error('Error checking current user:', err);
    }
  };

  const fetchUserDetails = async (userId) => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Fetch user details
      const userData = await safeFetch(`${API_URL}/api/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Fetch all departments for name lookup
      const departmentsData = await safeFetch(`${API_URL}/api/departments/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setUser(userData);
      setAllDepartments(departmentsData);
      setEditData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        middle_name: userData.middle_name || '',
        phone: userData.phone || '',
        user_type: userData.user_type || '',
        is_hidden: !!userData.is_hidden,
        works_with_tasks: !!userData.works_with_tasks,
        can_access_deferred_orders: canUserTypeAccessDeferredOrders(userData.user_type)
          && (
            typeof userData.can_access_deferred_orders === 'boolean'
              ? userData.can_access_deferred_orders
              : hasDefaultDeferredOrdersAccess(userData.user_type)
          ),
        user_status: userData.user_status || '',
        user_roles: userData.user_roles || '',
        available_menus: userData.available_menus || '',
        balance: userData.balance || 0
      });
      
      // Parse departments
      try {
        const departments = JSON.parse(userData.departments || '[]');
        setSelectedDepartments(departments);
        setUserDepartments(departments);
      } catch (e) {
        setSelectedDepartments([]);
        setUserDepartments([]);
      }
    } catch (err) {
      console.error('Ошибка загрузки пользователя:', err);
      setError(err.message || 'Ошибка загрузки пользователя');
    } finally {
      setLoading(false);
    }
  };

  // Add function to fetch user balance
  const fetchUserBalance = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const data = await safeFetch(`${API_URL}/api/transactions/balance?user_id=${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return data.balance || 0;
    } catch (err) {
      console.error('Ошибка загрузки баланса:', err);
      return 0;
    }
  };

  const handleInputChange = (field, value, target = null) => {
    let processedValue = value;
    
    if (field === 'phone') {
      processedValue = formatPhone(value);
      const phoneError = getPhoneError(processedValue);
      setEditErrors(prev => ({
        ...prev,
        phone: phoneError
      }));
      if (target && isFullPhoneNumber(processedValue)) {
        requestAnimationFrame(() => target.blur());
      }
    } else if (editErrors[field]) {
      setEditErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    setEditData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  const handleUserTypeChange = (nextUserType) => {
    haptic('selection');
    setEditData((prev) => {
      const hasDeferredAccessRole = canUserTypeAccessDeferredOrders(nextUserType);
      const defaultAccessGroupChanged = hasDefaultDeferredOrdersAccess(prev.user_type)
        !== hasDefaultDeferredOrdersAccess(nextUserType);
      return {
        ...prev,
        user_type: nextUserType,
        can_access_deferred_orders: hasDeferredAccessRole
          ? (defaultAccessGroupChanged
            ? hasDefaultDeferredOrdersAccess(nextUserType)
            : Boolean(prev.can_access_deferred_orders))
          : false,
      };
    });
  };

  const handleRoleToggle = (roleName) => {
    try {
      haptic('selection');
      const currentRoles = getNormalizedStageRoleNames(editData.user_roles);
      let updatedRoles;
      
      if (currentRoles.includes(roleName)) {
        updatedRoles = currentRoles.filter(role => role !== roleName);
      } else {
        updatedRoles = [...currentRoles, roleName];
      }
      
      setEditData(prev => ({
        ...prev,
        user_roles: JSON.stringify(updatedRoles)
      }));
    } catch (e) {
      console.error('Ошибка обработки ролей:', e);
    }
  };

  const getUserRoles = (useUserData = false) => {
    try {
      const rolesData = useUserData ? user?.user_roles : editData.user_roles;
      return getNormalizedStageRoleNames(rolesData);
    } catch (e) {
      return [];
    }
  };

  const handleMenuChange = (menuId, checked) => {
    try {
      haptic('selection');
      const currentMenus = JSON.parse(editData.available_menus || '[]');
      let updatedMenus;
      
      if (checked) {
        // Проверяем ограничения при добавлении
        const currentUserType = localStorage.getItem('user_type');
        if (menuId === 'management' && editData.user_type !== 'admin' && editData.user_type !== 'developer') {
          showToast('Раздел "Управление" доступен только администраторам и разработчикам', 'error');
          return;
        }
        if (menuId === 'orders-log' && !['admin', 'dispatcher', 'developer'].includes(editData.user_type)) {
          showToast('Раздел "Журнал заказов" доступен только диспетчерам, администраторам и разработчикам', 'error');
          return;
        }
        if (menuId === 'risk-control' && !['admin', 'dispatcher', 'developer'].includes(editData.user_type)) {
          showToast('Раздел "Контроль рисков" доступен только диспетчерам, администраторам и разработчикам', 'error');
          return;
        }
        updatedMenus = [...currentMenus, menuId];
      } else {
        // Запрещаем убирать настройки
        if (menuId === 'settings') {
          showToast('Пункт "Настройки" нельзя убирать', 'error');
          return;
        }
        updatedMenus = currentMenus.filter(id => id !== menuId);
      }
      
      const hasTasksMenu = updatedMenus.includes('tasks');
      setEditData(prev => ({
        ...prev,
        available_menus: JSON.stringify(updatedMenus),
        works_with_tasks: hasTasksMenu ? prev.works_with_tasks : false
      }));
    } catch (e) {
      console.error('Ошибка обработки меню:', e);
    }
  };

  const getSelectedMenus = () => {
    try {
      return JSON.parse(editData.available_menus || '[]');
    } catch (e) {
      return [];
    }
  };

  const parseList = (value) => {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const normalizeList = (items) => [...new Set(items.map(String))].sort().join('|');

  const accessFieldsChanged = (updateData) => {
    const oldDepartments = normalizeList(parseList(user?.departments));
    const newDepartments = normalizeList(parseList(updateData.departments));
    const oldRoles = normalizeList(parseList(user?.user_roles));
    const newRoles = normalizeList(parseList(updateData.user_roles));
    return oldDepartments !== newDepartments || oldRoles !== newRoles;
  };

  const performSave = async (updateData) => {
    const token = localStorage.getItem('token');

    await safeFetch(`${API_URL}/api/users/${user.id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    showToast('Пользователь успешно обновлен');
    setIsEditing(false);
    setShowRolesEdit(false);
    setShowMenuEdit(false);
    setShowDepartmentsEdit(false); // Close the departments edit panel
    setUserDepartments(selectedDepartments); // Update displayed departments
    setAssignmentImpact(null);
    setPendingUpdateData(null);

    await fetchUserDetails(user.id);

    if (currentUserId && user.id === currentUserId) {
      window.dispatchEvent(new CustomEvent('auth-refresh'));
    }

    if (onUserUpdated) {
      onUserUpdated();
    }
  };

  const ensurePhoneIsAvailable = async (nextPhone) => {
    const nextDigits = cleanPhoneDigits(nextPhone);
    const currentDigits = cleanPhoneDigits(user?.phone || '');
    if (!nextDigits || nextDigits === currentDigits) {
      return true;
    }

    const token = localStorage.getItem('token');
    const response = await safeFetch(`${API_URL}/api/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const users = response.users || [];
    const phoneExists = users.some(existingUser => (
      existingUser.id !== user.id &&
      existingUser.phone &&
      cleanPhoneDigits(existingUser.phone) === nextDigits
    ));

    if (phoneExists) {
      showToast('Пользователь с таким номером телефона уже существует', 'error');
      haptic('error');
      return false;
    }

    return true;
  };

  const handleSave = async (options = {}) => {
    haptic('tap');
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      const updateData = pendingUpdateData || {
        ...editData,
        departments: JSON.stringify(selectedDepartments) // Add departments to update data
      };

      if (editData.user_status === 'blocked' && user.user_status !== 'blocked') {
        updateData.blocked_date = new Date().toISOString();
      }

      const phoneError = updateData.phone
        ? getPhoneError(updateData.phone) || (!isFullPhoneNumber(updateData.phone) ? 'Номер должен начинаться с +79 и содержать 11 цифр' : '')
        : '';

      if (phoneError) {
        setEditErrors(prev => ({
          ...prev,
          phone: phoneError
        }));
        haptic('error');
        return;
      }

      if (!await ensurePhoneIsAvailable(updateData.phone)) {
        return;
      }

      if (!options.confirmed && accessFieldsChanged(updateData)) {
        const impact = await safeFetch(`${API_URL}/api/users/${user.id}/assignment-impact`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            departments: updateData.departments,
            user_roles: updateData.user_roles
          })
        });

        if (impact?.orders?.length > 0) {
          setPendingUpdateData(updateData);
          setAssignmentImpact(impact);
          return;
        }
      }

      await performSave(updateData);

    } catch (err) {
      console.error('Ошибка обновления пользователя:', err);
      showToast(err.message || 'Ошибка обновления пользователя', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    setResetting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await safeFetch(`${API_URL}/api/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setPasswordResetLink(response.reset_url || '');
      setPasswordResetExpiresAt(response.expires_at || '');
      setPasswordResetLogin(response.login || user?.login || '');
      setShowResetModal(true);
      
    } catch (err) {
      console.error('Ошибка сброса пароля:', err);
      showToast(err.message || 'Ошибка сброса пароля', 'error');
    } finally {
      setResetting(false);
    }
  };

  const handleBlock = async () => {
    if (!blockReason.trim()) {
      showToast('Укажите причину блокировки', 'error');
      return;
    }

    setBlocking(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const updateData = {
        user_status: 'blocked',
        block_reason: blockReason.trim(),
        blocked_date: new Date().toISOString()
      };

      await safeFetch(`${API_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      showToast('Пользователь заблокирован');
      setShowBlockModal(false);
      setBlockReason('');
      setBlockImpact(null);
      
      await fetchUserDetails(user.id);
      
      if (onUserUpdated) {
        onUserUpdated();
      }

      
    } catch (err) {
      console.error('Ошибка блокировки пользователя:', err);
      showToast(err.message || 'Ошибка блокировки пользователя', 'error');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    setBlocking(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const updateData = {
        user_status: 'active',
        block_reason: '',
        blocked_date: null
      };

      await safeFetch(`${API_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      showToast('Пользователь разблокирован');
      
      await fetchUserDetails(user.id);
      
      if (onUserUpdated) {
        onUserUpdated();
      }

      
    } catch (err) {
      console.error('Ошибка разблокировки пользователя:', err);
      showToast(err.message || 'Ошибка разблокировки пользователя', 'error');
    } finally {
      setBlocking(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow'
    });
  };

  const handleDeleteUser = async () => {
    if (!deleteReason.trim()) {
      showToast('Укажите причину удаления пользователя', 'error');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      await safeFetch(`${API_URL}/api/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: deleteReason.trim()
        })
      });

      showToast('Пользователь успешно удален');
      setDeleteImpact(null);
      setDeleteReason('');
      
      // Close the modal after a short delay and navigate back
      setTimeout(() => {
        if (onUserUpdated) onUserUpdated();
        navigate(-1);
      }, 1500);
      
    } catch (err) {
      console.error('Ошибка удаления пользователя:', err);
      showToast(err.message || 'Ошибка удаления пользователя', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreUser = async () => {
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      await safeFetch(`${API_URL}/api/users/${user.id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      showToast('Пользователь успешно восстановлен');
      
      // Обновляем данные пользователя
      await fetchUserDetails(user.id);
      
      if (onUserUpdated) {
        onUserUpdated();
      }
      
    } catch (err) {
      console.error('Ошибка восстановления пользователя:', err);
      showToast(err.message || 'Ошибка восстановления пользователя', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Function to get department names by IDs
  const getDepartmentNames = (departmentIds) => {
    if (!departmentIds || departmentIds.length === 0) return 'Нет назначенных подразделений';
    
    const departmentNames = departmentIds
      .map(id => {
        const dept = allDepartments.find(d => d.id === id);
        return dept ? dept.name : `ID: ${id}`;
      })
      .filter(name => name);
    
    return departmentNames.join(', ');
  };

  // Function to get user's department IDs
  const getUserDepartmentIds = (userData) => {
    try {
      return JSON.parse(userData.departments || '[]');
    } catch (e) {
      return [];
    }
  };

  const getDepartmentName = (departmentId) => {
    const dept = allDepartments.find(d => d.id === departmentId);
    return dept ? dept.name : `ID: ${departmentId || '—'}`;
  };

  const getImpactReasonText = (reasons = []) => {
    const labels = [];
    if (reasons.includes('removed_department')) labels.push('подразделение будет убрано');
    if (reasons.includes('removed_stage')) labels.push('этап будет убран');
    if (reasons.includes('user_blocked')) labels.push('назначение будет снято из-за блокировки');
    if (reasons.includes('user_deleted')) labels.push('назначение будет снято');
    return labels.join(', ') || 'доступ будет убран';
  };

  const openBlockUserModal = async () => {
    haptic('warning');
    setBlockReason('');
    setBlockImpact(null);
    setShowBlockModal(true);
    setCheckingBlockImpact(true);

    try {
      const token = localStorage.getItem('token');
      const impact = await safeFetch(`${API_URL}/api/users/${user.id}/block-impact`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBlockImpact(impact);
    } catch (err) {
      setShowBlockModal(false);
      showToast(err.message || 'Не удалось проверить назначенные заказы пользователя', 'error');
    } finally {
      setCheckingBlockImpact(false);
    }
  };

  const openDeleteUserModal = async () => {
    haptic('warning');
    setDeleteReason('');
    setDeleteImpact(null);
    setShowDeleteConfirmModal(true);
    setCheckingDeleteImpact(true);

    try {
      const token = localStorage.getItem('token');
      const impact = await safeFetch(`${API_URL}/api/users/${user.id}/delete-impact`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDeleteImpact(impact);
    } catch (err) {
      setShowDeleteConfirmModal(false);
      showToast(err.message || 'Не удалось проверить назначенные заказы пользователя', 'error');
    } finally {
      setCheckingDeleteImpact(false);
    }
  };

  // Эффект для автоматического управления доступом к разделу "Управление"
  useEffect(() => {
    if (isEditing) {
      try {
        // Если тип пользователя админ или разработчик, автоматически добавляем доступ к "Управление"
        if (editData.user_type === 'admin' || editData.user_type === 'developer') {
          const currentMenus = JSON.parse(editData.available_menus || '[]');
          // Проверяем, есть ли уже доступ к "Управление"
          if (!currentMenus.includes('management')) {
            const updatedMenus = [...currentMenus, 'management'];
            setEditData(prev => ({
              ...prev,
              available_menus: JSON.stringify(updatedMenus)
            }));
          }
        }
        // Если тип пользователя ниже админа, убираем доступ к "Управление"
        else if (editData.user_type !== 'admin' && editData.user_type !== 'developer') {
          const currentMenus = JSON.parse(editData.available_menus || '[]');
          const updatedMenus = currentMenus.filter(id => id !== 'management');
          
          if (currentMenus.length !== updatedMenus.length) {
            setEditData(prev => ({
              ...prev,
              available_menus: JSON.stringify(updatedMenus)
            }));
          }
        }
      } catch (e) {
        console.error('Ошибка при автоматическом управлении доступом:', e);
      }
    }
  }, [editData.available_menus, editData.user_type, isEditing]);

  useEffect(() => {
    if (isEditing && editData.user_type !== 'developer' && editData.is_hidden) {
      setEditData(prev => ({
        ...prev,
        is_hidden: false
      }));
    }
  }, [editData.user_type, editData.is_hidden, isEditing]);

  useEffect(() => {
    if (!isEditing || !editData.works_with_tasks) {
      return;
    }

    let selectedMenus = [];
    try {
      const parsedMenus = JSON.parse(editData.available_menus || '[]');
      selectedMenus = Array.isArray(parsedMenus) ? parsedMenus : [];
    } catch (e) {
      selectedMenus = [];
    }
    if (!selectedMenus.includes('tasks')) {
      setEditData(prev => ({
        ...prev,
        works_with_tasks: false
      }));
    }
  }, [editData.available_menus, editData.works_with_tasks, isEditing]);

  return (
    <>

      <div className="space-y-6 desktop-content-container-wide">
      {/* Основная информация */}
      {loading ? (
        <UserDetailsSkeleton />
      ) : (
        <>
          {/* Информация о пользователе */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-sm">
            {isEditing ? (
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
              >
                {/* Editing Mode */}
                <div className="-mx-4 -mt-4 border-b border-gray-100 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900 sm:mx-0 sm:mt-0 sm:border-0 sm:bg-transparent sm:p-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">
                        Профиль
                      </p>
                      <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
                        Редактирование пользователя
                      </h3>
                    </div>
                    <button
                      onClick={cancelEditing}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 active:scale-95 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                      aria-label="Закрыть редактирование"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Имя и фамилия */}
                <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-3 dark:border-gray-800 dark:bg-gray-950/30">
                  <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                      <Edit3 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Основное</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Имя, телефон и роль</p>
                    </div>
                  </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <FloatingField label="Имя">
                      <input
                        type="text"
                        value={editData.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        className={mobileInputClass('first_name')}
                        placeholder="Введите имя"
                      />
                    </FloatingField>
                  </div>
                  <div>
                    <FloatingField label="Фамилия">
                      <input
                      type="text"
                      value={editData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className={mobileInputClass('last_name')}
                      placeholder="Введите фамилию"
                      />
                    </FloatingField>
                  </div>
                </div>

                {/* Отчество и телефон */}
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <FloatingField label="Отчество">
                      <input
                      type="text"
                      value={editData.middle_name}
                      onChange={(e) => handleInputChange('middle_name', e.target.value)}
                      className={mobileInputClass('middle_name')}
                      placeholder="Введите отчество"
                      />
                    </FloatingField>
                  </div>
                  <div>
                    <FloatingField label="Телефон" error={!!editErrors.phone}>
                      <input
                      type="tel"
                      inputMode="tel"
                      value={editData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value, e.target)}
                      className={mobileInputClass('phone')}
                      placeholder="+7 (999) 123-45-67"
                      />
                    </FloatingField>
                    {editErrors.phone && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">{editErrors.phone}</p>
                    )}
                  </div>
                </div>

                {/* Тип пользователя */}
                <div className="mt-3">
                  <Dropdown
                      label="Тип пользователя"
                      value={editData.user_type}
                      onChange={handleUserTypeChange}
                      options={[
                        { value: 'user', label: 'Пользователь' },
                        { value: 'dispatcher', label: 'Диспетчер' },
                        { value: 'admin', label: 'Администратор' },
                        { value: 'developer', label: 'Разработчик' }
                      ]}
                      placeholder="Выберите тип"
                    />
                  </div>

                  {localStorage.getItem('user_type') === 'developer' && (
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                      <div className={`flex items-center justify-between gap-3 ${editData.user_type !== 'developer' ? 'opacity-60' : ''}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Скрытый разработчик
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Скрыт в списках для всех, кроме самого себя.
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={editData.user_type !== 'developer'}
                          onClick={() => {
                            if (editData.user_type !== 'developer') return;
                            haptic('selection');
                            setEditData(prev => ({
                              ...prev,
                              is_hidden: !prev.is_hidden
                            }));
                          }}
                          className={`toggle-switch shrink-0 disabled:opacity-60 ${editData.user_type !== 'developer' ? 'cursor-not-allowed' : ''}`}
                          style={{
                            '--toggle-bg': editData.user_type === 'developer' && editData.is_hidden
                              ? 'rgb(37 99 235)'
                              : 'rgb(156 163 175)',
                            '--toggle-height': '28px',
                            '--toggle-width': '48px',
                            '--knob-x': editData.user_type === 'developer' && editData.is_hidden ? '22px' : '2px'
                          }}
                          aria-pressed={editData.is_hidden}
                          aria-label="Скрытый разработчик"
                        >
                          <span className="toggle-switch-knob" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Доступные этапы (спойлер) */}
                <div className="space-y-2">
                  <button
                    onClick={() => toggleEditSection(showRolesEdit, setShowRolesEdit)}
                    className={editSectionButtonClass}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300">
                        <Truck className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Доступные этапы</span>
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                          {getUserRoles().length + (hasDeferredOrdersAccess() ? 1 : 0)} выбрано
                        </span>
                      </span>
                    </span>
                    {showRolesEdit ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  
                  <AnimatePresence initial={false}>
                    {showRolesEdit && (
                    <motion.div
                      className="grid grid-cols-1 gap-2 overflow-hidden sm:grid-cols-2"
                      initial={{ opacity: 0, height: 0, y: -4 }}
                      animate={{ opacity: 1, height: 'auto', y: 0 }}
                      exit={{ opacity: 0, height: 0, y: -4 }}
                      transition={{ duration: 0.18 }}
                    >
                      {USER_STAGE_ORDER.filter(key => isDryCleaningEnabled || key !== 'cleaning').map(key => {
                        const stage = orderStages[key];
                        if (!stage) return null;
                        const Icon = stage.icon;
                        const isSelected = getUserRoles().includes(stage.name);

                        return (
                          <label
                            key={key}
                            className={`${editOptionClass} ${
                              isSelected
                                ? 'border-blue-300 bg-blue-50 shadow-sm dark:border-blue-800 dark:bg-blue-900/20'
                                : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleRoleToggle(stage.name)}
                              className="rounded text-blue-600 focus:ring-blue-500"
                            />
                            <Icon className={`w-4 h-4 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                            <span className={`text-xs ${isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-600 dark:text-gray-400'}`}>
                              {stage.name}
                            </span>
                          </label>
                        );
                      })}

                      {canUserTypeAccessDeferredOrders(editData.user_type) && (() => {
                        const stage = orderStages.deferred;
                        const Icon = stage.icon;
                        const isSelected = hasDeferredOrdersAccess();
                        return (
                          <label
                            key="deferred"
                            className={`${editOptionClass} ${
                              isSelected
                                ? 'border-amber-300 bg-amber-50 shadow-sm dark:border-amber-800 dark:bg-amber-900/20'
                                : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                haptic('selection');
                                setEditData((prev) => ({
                                  ...prev,
                                  can_access_deferred_orders: !isSelected,
                                }));
                              }}
                              className="rounded text-amber-600 focus:ring-amber-500"
                            />
                            <Icon className={`w-4 h-4 ${isSelected ? 'text-amber-600' : 'text-gray-400'}`} />
                            <span className={`text-xs ${isSelected ? 'text-amber-900 dark:text-amber-100' : 'text-gray-600 dark:text-gray-400'}`}>
                              {stage.name}
                            </span>
                          </label>
                        );
                      })()}
                    </motion.div>
                  )}
                  </AnimatePresence>
                </div>

                {/* Доступные разделы меню (спойлер) */}
                <div className="space-y-2">
                  <button
                    onClick={() => toggleEditSection(showMenuEdit, setShowMenuEdit)}
                    className={editSectionButtonClass}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">
                        <Shield className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Разделы меню</span>
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{getSelectedMenus().length || 0} выбрано</span>
                      </span>
                    </span>
                    {showMenuEdit ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  
                  <AnimatePresence initial={false}>
                    {showMenuEdit && (
                    <motion.div
                      className="grid grid-cols-1 gap-2 overflow-hidden sm:grid-cols-2"
                      initial={{ opacity: 0, height: 0, y: -4 }}
                      animate={{ opacity: 1, height: 'auto', y: 0 }}
                      exit={{ opacity: 0, height: 0, y: -4 }}
                      transition={{ duration: 0.18 }}
                    >
                      {menuOptions.map(menu => {
                        const isChecked = getSelectedMenus().includes(menu.id);
                        const isSettings = menu.id === 'settings';
                        const isManagement = menu.id === 'management';
                        const isOrdersLog = menu.id === 'orders-log';
                        const isRiskControl = menu.id === 'risk-control';
                        const isUserAdminOrDeveloper = editData.user_type === 'admin' || editData.user_type === 'developer';
                        const canUseOrdersLog = ['admin', 'dispatcher', 'developer'].includes(editData.user_type);
                        const canUseRiskControl = ['admin', 'dispatcher', 'developer'].includes(editData.user_type);
                        
                        return (
                          <label
                            key={menu.id}
                            className={`${editOptionClass} ${
                              isChecked 
                                ? 'border-blue-300 bg-blue-50 shadow-sm dark:border-blue-800 dark:bg-blue-900/20' 
                                : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleMenuChange(menu.id, e.target.checked)}
                              disabled={
                                (isSettings && isChecked) || 
                                (isManagement && !isUserAdminOrDeveloper) ||
                                (isOrdersLog && !canUseOrdersLog) ||
                                (isRiskControl && !canUseRiskControl) ||
                                saving
                              }
                              className="rounded text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                            />
                            <span className={`text-xs ${
                              isChecked 
                                ? 'text-blue-900 dark:text-blue-100' 
                                : isSettings && isChecked 
                                  ? 'text-gray-500 dark:text-gray-400' 
                                  : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {menu.label}
                              {isSettings && isChecked && <span className="text-xs text-gray-400 ml-1"></span>}
                            </span>
                          </label>
                        );
                      })}
                    </motion.div>
                  )}
                  </AnimatePresence>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Работает с поручениями</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditData((prev) => {
                        const nextWorksWithTasks = !prev.works_with_tasks;
                        const selectedMenus = parseList(prev.available_menus);
                        const nextMenus = nextWorksWithTasks && !selectedMenus.includes('tasks')
                          ? [...selectedMenus, 'tasks']
                          : selectedMenus;

                        return {
                          ...prev,
                          works_with_tasks: nextWorksWithTasks,
                          available_menus: JSON.stringify(nextMenus)
                        };
                      })}
                      className="toggle-switch shrink-0"
                      style={{
                        '--toggle-bg': editData.works_with_tasks ? '#2563eb' : '#d1d5db',
                        '--knob-x': editData.works_with_tasks ? '22px' : '2px'
                      }}
                      aria-pressed={editData.works_with_tasks}
                    >
                      <span className="sr-only">Работает с поручениями</span>
                      <span className="toggle-switch-knob" />
                    </button>
                  </div>
                </div>

                {/* Подразделения пользователя (спойлер) */}
                <div className="space-y-2">
                  <button
                    onClick={() => toggleEditSection(showDepartmentsEdit, setShowDepartmentsEdit)}
                    className={editSectionButtonClass}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
                        <Building className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Подразделения</span>
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{selectedDepartments.length || 0} выбрано</span>
                      </span>
                    </span>
                    {showDepartmentsEdit ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  
                  <AnimatePresence initial={false}>
                    {showDepartmentsEdit && (
                    <motion.div
                      className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50/60 p-2 dark:border-gray-800 dark:bg-gray-950/30"
                      initial={{ opacity: 0, height: 0, y: -4 }}
                      animate={{ opacity: 1, height: 'auto', y: 0 }}
                      exit={{ opacity: 0, height: 0, y: -4 }}
                      transition={{ duration: 0.18 }}
                    >
                      <DepartmentSelector
                        userId={user.id}
                        selectedDepartments={selectedDepartments}
                        onChange={setSelectedDepartments}
                        disabled={saving}
                      />
                    </motion.div>
                  )}
                  </AnimatePresence>
                </div>

                {/* Кнопки сохранения и отмены */}
                <div className="-mx-4 -mb-4 flex flex-row-reverse gap-2 border-t border-gray-100 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900 sm:mx-0 sm:mb-0 sm:border-t sm:bg-transparent sm:px-0 sm:pb-0 sm:justify-start">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex min-h-[44px] flex-1 sm:flex-initial sm:px-6 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Сохранение...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Сохранить</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={cancelEditing}
                    className="min-h-[44px] sm:px-6 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 active:scale-[0.99] dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    Отмена
                  </button>
                </div>
              </motion.div>
            ) : (
              <div className="space-y-4">
                {/* View Mode */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Имя:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{user?.first_name || 'Не указано'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Фамилия:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{user?.last_name || 'Не указано'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Отчество:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{user?.middle_name || 'Не указано'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Телефон:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{user?.phone || 'Не указан'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Логин:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">@{user?.login}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Дата создания:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(user?.created_date)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Последняя активность:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(user?.last_activity)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Версия приложения:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {user?.last_client_version || 'Не определена'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Версия проверена:</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {user?.client_version_checked_at ? formatDate(user.client_version_checked_at) : 'Не проверялась'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Баланс:</span>
                    <p className={`font-medium ${user?.balance < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {Number(user?.balance || 0).toFixed(2)} ₽
                    </p>
                  </div>
                </div>

                {/* Статус и тип */}
                <div className="flex items-center justify-between gap-3 py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${userTypeColors[user?.user_type]}`}>
                      {userTypeLabels[user?.user_type]}
                    </div>
                    <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColors[user?.user_status]}`}>
                      {statusLabels[user?.user_status]}
                    </div>
                  </div>
                </div>

                {/* Доступные этапы */}
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-sm block mb-2">Доступные этапы:</span>
                  <div className="flex flex-wrap gap-2">
                    {user && (getUserRoles(true).length > 0 || hasDeferredOrdersAccess(true)) ? (
                      <>
                        {USER_STAGE_ORDER
                          .filter(key => isDryCleaningEnabled || key !== 'cleaning')
                          .filter(key => getUserRoles(true).includes(orderStages[key]?.name))
                          .map(key => {
                          const stage = orderStages[key];
                          if (!stage) return null;
                          const Icon = stage.icon;
                          return (
                            <div
                              key={key}
                              className={`inline-flex items-center px-2 py-1 rounded-lg text-xs ${stage.color}`}
                            >
                              <Icon className="w-3 h-3 mr-1" />
                              <span>{stage.name}</span>
                            </div>
                          );
                          })}
                        {hasDeferredOrdersAccess(true) && (() => {
                          const stage = orderStages.deferred;
                          const Icon = stage.icon;
                          return (
                            <div
                              key="deferred"
                              className={`inline-flex items-center px-2 py-1 rounded-lg text-xs ${stage.color}`}
                            >
                              <Icon className="w-3 h-3 mr-1" />
                              <span>{stage.name}</span>
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400 text-xs italic">Нет доступных этапов</span>
                    )}
                  </div>
                </div>

                {/* Доступные разделы меню */}
                {user?.available_menus && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm block mb-2">Доступные разделы меню:</span>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        try {
                          const availableMenus = JSON.parse(user.available_menus || '[]');
                          if (availableMenus.length === 0) {
                            return <span className="text-gray-400 dark:text-gray-500 text-xs italic">Доступных разделов нет</span>;
                          }
                          // Фильтруем menuOptions чтобы сохранить правильный порядок
                          return menuOptions
                            .filter(menu => availableMenus.includes(menu.id))
                            .map(menu => (
                              <span
                                key={menu.id}
                                className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 rounded-lg text-xs"
                              >
                                {menu.label}
                              </span>
                            ));
                        } catch (e) {
                          return <span className="text-gray-500 text-xs">Ошибка загрузки меню</span>;
                        }
                      })()}
                    </div>
                  </div>
                )}

                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-sm block mb-2">Работа с поручениями:</span>
                  <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${
                    user?.works_with_tasks
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    {user?.works_with_tasks ? 'Назначать можно' : 'Назначать нельзя'}
                  </span>
                </div>

                {/* Причина блокировки */}
                {user?.block_reason && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      <strong>Причина блокировки:</strong> {user.block_reason}
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Заблокирован: {formatDate(user.blocked_date)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Подразделения пользователя (view mode) - только в режиме просмотра */}
            {!isEditing && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Building className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Подразделения
                  </h3>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {getDepartmentNames(getUserDepartmentIds(user))}
                </div>
              </div>
            )}

            
          </div>

          {/* Действия */}
          {!isEditing && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Действия
              </h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:flex lg:flex-wrap lg:items-center">
                <button
                  onClick={startEditing}
                  className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-blue-600 text-white rounded-lg lg:rounded-xl hover:bg-blue-700 active:scale-[0.98] transition-all text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                >
                  <Edit3 className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                  <span>Редактировать</span>
                </button>

                <button
                  onClick={() => setShowHistoryModal(true)}
                  className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-violet-600 text-white rounded-lg lg:rounded-xl hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-500 transition-colors text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                >
                  <Clock className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                  <span>История</span>
                </button>

                {['admin', 'developer'].includes(localStorage.getItem('user_type')) && (
                  <button
                    onClick={() => setShowActivityModal(true)}
                    className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-cyan-600 text-white rounded-lg lg:rounded-xl hover:bg-cyan-700 dark:bg-cyan-600 dark:hover:bg-cyan-500 transition-colors text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                  >
                    <Activity className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                    <span>Активность</span>
                  </button>
                )}

                <button
                  onClick={() => setShowResetConfirmModal(true)}
                  disabled={resetting}
                  className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-orange-600 text-white rounded-lg lg:rounded-xl hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                >
                  {resetting ? (
                    <Loader2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 animate-spin shrink-0" />
                  ) : (
                    <RotateCcw className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                  )}
                  <span>Сбросить пароль</span>
                </button>
                
                {user?.user_status === 'active' && (
                  currentUserId && currentUserId !== user?.id ? (
                    <button
                      onClick={openBlockUserModal}
                      disabled={blocking}
                      className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-red-600 text-white rounded-lg lg:rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                    >
                      {blocking ? (
                        <Loader2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 animate-spin shrink-0" />
                      ) : (
                        <UserX className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                      )}
                      <span>Заблокировать</span>
                    </button>
                  ) : (
                    <button
                      disabled={true}
                      className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg lg:rounded-xl cursor-not-allowed text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                    >
                      <UserX className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                      <span>Заблокировать</span>
                    </button>
                  )
                )}
                
                {user?.user_status === 'blocked' && (
                  currentUserId && currentUserId !== user?.id ? (
                    <button
                      onClick={handleUnblock}
                      disabled={blocking}
                      className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-green-600 text-white rounded-lg lg:rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                    >
                      {blocking ? (
                        <Loader2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 animate-spin shrink-0" />
                      ) : (
                        <UserCheck className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                      )}
                      <span>Разблокировать</span>
                    </button>
                  ) : (
                    <button
                      disabled={true}
                      className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg lg:rounded-xl cursor-not-allowed text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                    >
                      <UserCheck className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                      <span>Разблокировать</span>
                    </button>
                  )
                )}
                
                {user?.user_status === 'deleted' && (
                  localStorage.getItem('user_type') === 'developer' ? (
                    <button
                      onClick={() => setShowRestoreConfirmModal(true)}
                      disabled={saving}
                      className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-green-600 text-white rounded-lg lg:rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs lg:text-sm font-medium col-span-2 lg:col-span-1 lg:flex-1 lg:min-w-[140px]"
                    >
                      {saving ? (
                        <Loader2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 animate-spin shrink-0" />
                      ) : (
                        <UserCheck className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                      )}
                      <span>Восстановить</span>
                    </button>
                  ) : (
                    <button
                      disabled={true}
                      className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg lg:rounded-xl cursor-not-allowed text-xs lg:text-sm font-medium col-span-2 lg:col-span-1 lg:flex-1 lg:min-w-[140px]"
                    >
                      <UserCheck className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                      <span>Удален</span>
                    </button>
                  )
                )}
                
                {/* Delete button for developers */}
                {localStorage.getItem('user_type') === 'developer' && user?.user_type !== 'developer' && user?.user_status !== 'deleted' && (
                    currentUserId && currentUserId !== user?.id ? (
                      <button
                        onClick={openDeleteUserModal}
                        disabled={saving}
                        className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-red-800 text-white rounded-lg lg:rounded-xl hover:bg-red-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                      >
                      {saving ? (
                        <Loader2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 animate-spin shrink-0" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                      )}
                      <span>Удалить</span>
                    </button>
                  ) : (
                    <button
                      disabled={true}
                      className="flex min-h-[42px] lg:h-11 items-center justify-center space-x-2 px-3 py-2 lg:px-4 lg:py-2.5 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg lg:rounded-xl cursor-not-allowed text-xs lg:text-sm font-medium lg:flex-1 lg:min-w-[140px]"
                    >
                      <Trash2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" />
                      <span>Удалить</span>
                    </button>
                  )
                )}
              </div>
            </div>
          )}


          {/* Модальное окно подтверждения сброса пароля */}
          <ModalShell
            open={showResetConfirmModal}
            onClose={() => setShowResetConfirmModal(false)}
            variant="sheet"
            panelClassName="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Сброс пароля</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Подтвердите действие</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Вы уверены, что хотите сбросить пароль пользователя <strong>{formatPersonName(user, user?.login || '')}</strong>?
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Будет создана одноразовая ссылка с ограниченным сроком действия.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  haptic('close');
                  setShowResetConfirmModal(false);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  haptic('selection');
                  setShowResetConfirmModal(false);
                  handleResetPassword();
                }}
                disabled={resetting}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {resetting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Сбрасываю...</span>
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4" />
                    <span>Сбросить</span>
                  </>
                )}
              </button>
            </div>
          </ModalShell>

          {/* Модальное окно подтверждения удаления пользователя */}
          <ModalShell
            open={showDeleteConfirmModal}
            onClose={() => {
              haptic('close');
              setShowDeleteConfirmModal(false);
              setDeleteImpact(null);
              setDeleteReason('');
            }}
            variant="sheet"
            panelClassName="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Удаление пользователя</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Подтвердите действие</p>
                </div>
              </div>

              {checkingDeleteImpact ? (
                <div className="space-y-3 py-3" role="status" aria-label="Проверка назначенных заказов">
                  <SkeletonBlock className="h-4 w-2/3" />
                  <SkeletonBlock className="h-16 w-full rounded-xl" />
                  <SkeletonBlock className="h-16 w-full rounded-xl" />
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Вы уверены, что хотите удалить пользователя <strong>{formatPersonName(user, user?.login || '')}</strong>?
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Пользователь будет помечен как удаленный, но его данные сохранятся в системе.
                    </p>
                  </div>

                  {deleteImpact?.orders?.length > 0 && (
                    <>
                      <div className="mb-4 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 p-3">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          На пользователя назначены заказы. После удаления они перейдут в нераспределённые.
                        </p>
                      </div>

                      <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 mb-4">
                        {deleteImpact.orders.map((order, index) => (
                          <div key={order.id || index} className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <div className="flex items-center justify-between gap-3 text-sm">
                              <span className="font-medium text-gray-900 dark:text-gray-100">№{order.order_number || order.id}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{order.status_label || order.status}</span>
                            </div>
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {getDepartmentName(order.department_id)} · {getImpactReasonText(order.reasons)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="mb-5">
                    <FloatingField label="Причина удаления">
                      <textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 dark:text-gray-100 text-sm"
                        rows={3}
                        placeholder="Введите причину удаления..."
                      />
                    </FloatingField>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        haptic('close');
                        setShowDeleteConfirmModal(false);
                        setDeleteImpact(null);
                        setDeleteReason('');
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      Отмена
                    </button>
                    <button
                      onClick={handleDeleteUser}
                      disabled={saving || !deleteReason.trim()}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Удаляю...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          <span>Удалить</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </ModalShell>

          {/* Модальное окно сброса пароля */}
          <ModalShell
            open={showResetModal}
            onClose={() => {
              haptic('close');
              setShowResetModal(false);
              setPasswordResetLink('');
              setPasswordResetExpiresAt('');
              setPasswordResetLogin('');
            }}
            variant="sheet"
            panelClassName="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ссылка для сброса готова</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Пользователь сам задаст новый пароль</p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Одноразовая ссылка:</p>
                <textarea
                  readOnly
                  value={passwordResetLink}
                  className="min-h-[112px] w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    onClick={handleCopyResetLink}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <Copy className="h-4 w-4" />
                    <span>Копировать</span>
                  </button>
                  <button
                    onClick={handleShareResetLink}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <Share2 className="h-4 w-4" />
                    <span>Поделиться</span>
                  </button>
                </div>
              </div>
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Важно:</strong> ссылка одноразовая и {passwordResetExpiresAt ? `действует до ${formatDate(passwordResetExpiresAt)}` : 'действует ограниченное время'}.
                </p>
              </div>
              
              <button
                onClick={() => {
                  haptic('close');
                  setShowResetModal(false);
                  setPasswordResetLink('');
                  setPasswordResetExpiresAt('');
                  setPasswordResetLogin('');
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Понятно
              </button>
            </div>
          </ModalShell>

          {/* Модальное окно блокировки */}
          <ModalShell
            open={showBlockModal}
            onClose={() => {
              haptic('close');
              setShowBlockModal(false);
              setBlockReason('');
            }}
            variant="sheet"
            panelClassName="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <UserX className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Блокировка пользователя</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Укажите причину блокировки</p>
                </div>
              </div>
              
              <div className="mb-4">
                {checkingBlockImpact ? (
                  <div className="mb-3 space-y-2 rounded-lg bg-yellow-50 px-3 py-3 dark:bg-yellow-900/20" role="status" aria-label="Проверка назначенных заказов">
                    <SkeletonBlock className="h-3.5 w-3/5 bg-yellow-200 dark:bg-yellow-900/60" />
                    <SkeletonBlock className="h-10 w-full bg-yellow-100 dark:bg-yellow-900/40" />
                  </div>
                ) : blockImpact?.orders?.length > 0 ? (
                  <>
                    <div className="mb-3 rounded-lg bg-yellow-50 p-3 dark:bg-yellow-900/20">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        После блокировки у пользователя будут сняты назначенные заказы, потому что он больше не сможет с ними работать.
                      </p>
                    </div>
                    <div className="mb-3 max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                      {blockImpact.orders.map((order, index) => (
                        <div key={order.id || index} className="border-b border-gray-100 px-3 py-2 last:border-0 dark:border-gray-700">
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <span className="font-medium text-gray-900 dark:text-gray-100">№{order.order_number || order.id}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{order.status_label || order.status}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {getDepartmentName(order.department_id)} · {getImpactReasonText(order.reasons)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
                <FloatingField label="Причина блокировки">
                  <textarea
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 dark:text-gray-100 text-sm"
                    rows={3}
                    placeholder="Введите причину блокировки..."
                  />
                </FloatingField>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    haptic('close');
                    setShowBlockModal(false);
                    setBlockReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Отмена
                </button>
                <button
                  onClick={handleBlock}
                  disabled={blocking || checkingBlockImpact || !blockReason.trim()}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {blocking ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Блокирую...</span>
                    </>
                  ) : (
                    <>
                      <UserX className="w-4 h-4" />
                      <span>Заблокировать</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </ModalShell>

          {/* Модальное окно подтверждения восстановления пользователя */}
          <ModalShell
            open={Boolean(user?.user_status === 'deleted' && showRestoreConfirmModal)}
            onClose={() => {
              haptic('close');
              setShowRestoreConfirmModal(false);
            }}
            variant="sheet"
            panelClassName="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Восстановление пользователя</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Подтвердите действие</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Вы уверены, что хотите восстановить пользователя <strong>{formatPersonName(user, user?.login || '')}</strong>?
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Пользователь будет восстановлен и получит статус "Активен".
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    haptic('close');
                    setShowRestoreConfirmModal(false);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Отмена
                </button>
                <button
                  onClick={() => {
                    haptic('selection');
                    setShowRestoreConfirmModal(false);
                    handleRestoreUser();
                  }}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Восстанавливаю...</span>
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4" />
                      <span>Восстановить</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </ModalShell>
        </>
      )}

      {/* Предупреждение о снятии назначенных заказов */}
      <ModalShell
        open={Boolean(assignmentImpact?.orders?.length > 0)}
        onClose={() => {
          haptic('close');
          setAssignmentImpact(null);
          setPendingUpdateData(null);
        }}
        variant="sheet"
        panelClassName="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full shadow-xl"
      >
        <div>
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Будут сняты назначенные заказы</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Подтвердите изменение доступа</p>
            </div>
          </div>

          <div className="mb-4 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 p-3">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              У пользователя есть заказы, которые перестанут соответствовать его подразделениям или этапам. После сохранения они перейдут в нераспределённые.
            </p>
          </div>

          <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 mb-5">
            {assignmentImpact?.orders?.map((order, index) => (
              <div key={order.id || index} className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100">№{order.order_number || order.id}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{order.status_label || order.status}</span>
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {getDepartmentName(order.department_id)} · {getImpactReasonText(order.reasons)}
                </div>
              </div>
            ))}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => {
                haptic('close');
                setAssignmentImpact(null);
                setPendingUpdateData(null);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              Отмена
            </button>
            <button
              onClick={() => {
                haptic('selection');
                handleSave({ confirmed: true });
              }}
              disabled={saving}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Сохраняю...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Сохранить и снять назначения</span>
                </>
              )}
            </button>
          </div>
        </div>
      </ModalShell>
      
      {/* Модальное окно для ошибок */}


      <UserActivityModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        userId={user?.id}
        userName={user ? formatPersonName(user, user?.login || 'Без имени') : ''}
        departments={allDepartments}
      />

      <UserActivityModal
        open={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        userId={user?.id}
        userName={user ? formatPersonName(user, user?.login || 'Без имени') : ''}
        departments={allDepartments}
        mode="activity"
        lastActivity={user?.last_activity}
      />
    </div>
    </>
  );
};

export default UserDetailsCompact;
